import { useEffect, useState, useCallback } from "react";

// Two independent axes:
//   theme:    "light" | "dark"        — mapped to :root[data-theme="dark"]
//   contrast: "standard" | "high"     — mapped to :root[data-contrast="high"]
//
// Persisted in localStorage so the user's choice sticks across sessions.
// Applied to <html> as a data-attribute so the CSS variables from
// design-tokens.css can flip without any JS re-render of components.

const THEME_KEY = "rt.appearance.theme";
const CONTRAST_KEY = "rt.appearance.contrast";

const readStored = (key, fallback) => {
  if (typeof window === "undefined") return fallback;
  try {
    return window.localStorage.getItem(key) || fallback;
  } catch {
    return fallback;
  }
};

// Detect the OS-level preference so first-time visitors whose phone/desktop is
// already in dark mode get dark automatically. Only used when the user has
// never set a preference in this app (i.e. no localStorage key yet). Once they
// pick something in Profile → Appearance, we respect that forever.
const detectOsTheme = () => {
  if (typeof window === "undefined" || !window.matchMedia) return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
};

const readInitialTheme = () => {
  if (typeof window === "undefined") return "light";
  try {
    const stored = window.localStorage.getItem(THEME_KEY);
    if (stored) return stored;
  } catch { /* localStorage disabled */ }
  return detectOsTheme();
};

const applyToRoot = (theme, contrast) => {
  const el = document.documentElement;
  if (theme === "dark") el.setAttribute("data-theme", "dark");
  else el.removeAttribute("data-theme");
  if (contrast === "high") el.setAttribute("data-contrast", "high");
  else el.removeAttribute("data-contrast");
};

export const useAppearance = () => {
  const [theme, setTheme] = useState(readInitialTheme);
  const [contrast, setContrast] = useState(() => readStored(CONTRAST_KEY, "standard"));

  useEffect(() => {
    applyToRoot(theme, contrast);
    try { window.localStorage.setItem(THEME_KEY, theme); } catch { /* ignore */ }
    try { window.localStorage.setItem(CONTRAST_KEY, contrast); } catch { /* ignore */ }
  }, [theme, contrast]);

  const toggleTheme = useCallback(() => {
    setTheme((t) => (t === "dark" ? "light" : "dark"));
  }, []);
  const toggleContrast = useCallback(() => {
    setContrast((c) => (c === "high" ? "standard" : "high"));
  }, []);

  return { theme, contrast, setTheme, setContrast, toggleTheme, toggleContrast };
};
